import React, { useMemo } from 'react';
import { useTypedSelector } from '../../shared/hooks/useTypedSelector';
import {
    DialogContent,
    Grid,
    makeStyles,
    Typography,
    Button,
    DialogActions,
} from '@material-ui/core';
import SizeChart from './SizeChart';
import { ITheme } from '../../themes/custom/ITheme';
import SelectFabric from './SelectFabric';
import ProofServiceBlock from './ProofServiceBlock';
import CartSummary from './CartSummary';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import * as actions from '../../store/actions';
import i18next from 'i18next';
import SnackBarType from '../../objects/app/snackbar';
import { useDispatch } from 'react-redux';

interface Props {
    customerId: number | null;
    guestId: number | null;
    sizeImg: string;
    proofServiceLink: string;
    onSubmit: (addProofService: boolean) => void;
    openTeamDialog: () => void;
    closeTeamDialog: () => void;
    isMobile: boolean;
    admin: boolean;
}

const useStyles = makeStyles((theme: ITheme) => {
    return {
        dialog: {},
        closeButton: {
            color: theme.custom.palette.secondary,
        },
        dialogActions: {
            padding: '17px 37px',
        },
        input: {
            width: '50px',
        },
        size: {
            background: 'rgba(0, 0, 0, 0.03)',
            padding: '5px',
        },
        sizeTitle: {
            fontWeight: 'bold',
        },
        column: {
            width: '50%',
        },
        h6: {
            fontSize: '24px',
            fontWeight: 300,
        },
        bottomButton: {
            lineHeight: '24px',
            size: '20px',
            fontWeight: 400,
        },
        teamButton: {
            marginLeft: '20px',
            textTransform: 'none',
            padding: '0 25px',
        },
        smallDisplayButton: {
            width: '100%',
            fontSize: '12px',
            padding: '5px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
        },
        smallDisplayGridButton: {
            width: '100%',
        },
        smallDisplayDialogActions: {
            padding: '10px',
        },
    };
});

const BuyDialog: React.FC<Props> = (props) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const {
        admin,
        onSubmit,
        sizeImg,
        proofServiceLink,
        openTeamDialog,
        isMobile,
    } = props;

    const { data: teamData } = useTypedSelector((state) => state.od.team);

    const { data: selectedData } = useTypedSelector(
        (state) => state.od.selected
    );
    const sumQuantity = useMemo<number>((): number => {
        if (
            teamData !== null &&
            teamData.filter(
                (teamMember) =>
                    teamMember.size !== null ||
                    teamMember.name.length !== 0 ||
                    teamMember.number.length !== 0
            ).length > 0
        ) {
            return teamData.filter((teamMember) => teamMember.size !== null)
                .length;
        } else {
            return selectedData && selectedData.sizes
                ? selectedData.sizes
                      .map((size) => size.quantity)
                      .reduce((a, b) => a + b, 0)
                : 0;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedData, teamData]);

    const isTeam = useMemo<boolean>((): boolean => {
        return teamData &&
            teamData.filter(
                (teamMember) =>
                    teamMember.size !== null ||
                    teamMember.name.length !== 0 ||
                    teamMember.number.length !== 0
            ).length > 0
            ? true
            : false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teamData]);

    const handleOnSubmit = (addProofService: boolean) => {
        onSubmit(addProofService);
    };

    const handleNoSize = () => {
        dispatch(
            actions.addSnackbar(
                i18next.t('Please add at least one size.'),
                SnackBarType.WARNING,
                false
            )
        );
    };

    return (
        <>
            <DialogContent className={classes.dialog}>
                <Grid justifyContent="space-between" container wrap="nowrap">
                    <Grid item xs={12}>
                        <Typography variant="h6">
                            {t('1. Select fabric')}
                        </Typography>
                        <SelectFabric />

                        <Typography variant="h6">
                            {t('2. Add your sizes')}
                            {isTeam && (
                                <Button
                                    onClick={openTeamDialog}
                                    variant="outlined"
                                    color="secondary"
                                    size="small"
                                    type="submit"
                                    className={classes.teamButton}
                                >
                                    {t('Edit names, numbers and sizes')}
                                </Button>
                            )}
                        </Typography>

                        <SizeChart sizeImg={sizeImg} isMobile={isMobile} />

                        {isMobile && (
                            <React.Fragment>
                                <ProofServiceBlock
                                    proofServiceLink={proofServiceLink}
                                />
                                <CartSummary isMobile={isMobile} />
                            </React.Fragment>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions
                className={clsx(
                    classes.dialogActions,
                    isMobile && classes.smallDisplayDialogActions
                )}
            >
                <Grid container>
                    {!isMobile && (
                        <Grid item xs={12}>
                            <Grid justifyContent="space-between" container>
                                <Grid item xs={4}>
                                    <ProofServiceBlock
                                        proofServiceLink={proofServiceLink}
                                    />
                                </Grid>
                                <Grid item xs={8}>
                                    <CartSummary isMobile={isMobile} />
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Grid
                            container
                            direction="row-reverse"
                            spacing={2}
                            {...(sumQuantity === 0 && {
                                onClick: handleNoSize,
                            })}
                        >
                            <Grid
                                item
                                {...(isMobile && { sm: 12 })}
                                className={clsx(
                                    isMobile && classes.smallDisplayGridButton
                                )}
                            >
                                <Button
                                    variant="contained"
                                    className={clsx(
                                        classes.bottomButton,
                                        isMobile && classes.smallDisplayButton
                                    )}
                                    onClick={() => handleOnSubmit(true)}
                                    {...((sumQuantity === 0 || admin) && {
                                        disabled: true,
                                    })}
                                >
                                    {t('Add to cart with proof service')}
                                </Button>
                            </Grid>
                            <Grid
                                item
                                className={clsx(
                                    isMobile && classes.smallDisplayGridButton
                                )}
                                {...(isMobile && { sm: 12 })}
                            >
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    className={clsx(
                                        classes.bottomButton,
                                        isMobile && classes.smallDisplayButton
                                    )}
                                    onClick={() => handleOnSubmit(false)}
                                    {...((sumQuantity === 0 || admin) && {
                                        disabled: true,
                                    })}
                                >
                                    {t('Add to Cart')}
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogActions>
        </>
    );
};

export default BuyDialog;
